import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { NON_BREAKING_SPACE } from 'utils/constants/common';

import { TokenBaseStaticSpace } from '@hh.ru/magritte-design-tokens/types';
import {
    Button,
    Card,
    Cell,
    CellText,
    createDataProvider,
    RenderContentBefore,
    RenderOptionType,
    Select,
    SelectOption,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import fetchActiveVacancies from 'components/common/VacanciesSelect/fetchActiveVacancies';

import { setIsVacancyForeignArea } from 'store/autoReducers';
import { useDispatch, useSelector } from 'store/utils';

const SELECT_VACANCIES_THROTTLE = 500;

interface SelectOptionItem {
    value: string;
    text: string;
    areaName: string;
    foreignArea: string;
}
interface MagritteVacanciesSelectProps {
    currentVacancyId: string | null;
    currentVacancyName?: string | null;
    chooseVacancy: (vacancyId: string, vacancyName: string) => void;
    resetVacancy: () => void;
    onOpenVacancies?: () => void;
    onSearch?: (options: SelectOption<SelectOptionItem>[]) => void;
    label?: string;
    error?: string;
    under?: ReactNode;
    vSpacingBottom?: TokenBaseStaticSpace;
    withClearButton?: boolean;
    disabled?: boolean;
}

const NAME_SEPARATOR = ', ';

const TrlKeys = {
    label: 'create.campaign.select.vacancy.label',
    placeholder: 'create.campaign.select.vacancy.placeholder',
    searchPlaceholder: 'create.campaign.input.vacancy.placeholder',
    notFound: 'create.campaign.select.vacancy.notFound',
    option: 'create.campaign.select.vacancyId.option',
    clearButton: 'create.campaign.select.vacancy.clearButton',
};

export const MagritteVacanciesSelect: FC<MagritteVacanciesSelectProps> = ({
    currentVacancyId,
    currentVacancyName,
    chooseVacancy,
    onOpenVacancies,
    onSearch,
    label,
    error,
    under,
    vSpacingBottom,
    resetVacancy,
    withClearButton,
    disabled,
}) => {
    const isEmployer = useSelector((state) => !!state.user.user?.employerId);
    const [value, setValue] = useState<SelectOptionItem | undefined>(undefined);

    const trl = useTrls();
    const dispatch = useDispatch();
    const { isXS } = useBreakpoint();

    const onChangeSelect = useCallback(
        (option: SelectOptionItem) => {
            const { value, text, foreignArea } = option ?? {};
            setValue(value && text ? { value, text, areaName: '', foreignArea: '' } : undefined);
            chooseVacancy(value ?? '', text ?? '');
            dispatch(setIsVacancyForeignArea(foreignArea === 'true'));
        },
        [chooseVacancy, dispatch]
    );

    const [isDisabled, setIsDisabled] = useState(false);

    useEffect(() => {
        if (currentVacancyId && currentVacancyName) {
            setValue({
                value: currentVacancyId,
                text: currentVacancyName,
                areaName: '',
                foreignArea: '',
            });
        } else {
            setValue(undefined);
        }
    }, [currentVacancyId, currentVacancyName]);

    const dataProvider = useMemo(
        () =>
            createDataProvider<{ value: string; text: string; areaName: string; foreignArea: string }>({
                minCharsCount: 0,
                debounceTimeout: SELECT_VACANCIES_THROTTLE,
                fetcher: async (filterValue: string) => {
                    const vacancies = await fetchActiveVacancies(isEmployer, filterValue);
                    if (vacancies.length === 0) {
                        setIsDisabled(true);
                    }
                    return [
                        {
                            type: 'cells',
                            items: vacancies.map(({ vacancyId, name, areaName, foreignArea }) => ({
                                value: String(vacancyId),
                                data: {
                                    value: String(vacancyId),
                                    text: name,
                                    areaName,
                                    foreignArea: String(foreignArea),
                                },
                            })),
                        },
                    ];
                },
            }),
        [isEmployer]
    );

    const customRenderItem: RenderOptionType<{ value: string; text: string; areaName: string; foreignArea: string }> =
        useCallback(
            ({ input, data: { text, areaName, value } }) => {
                return (
                    <Cell left={isXS ? undefined : input} right={isXS ? input : undefined}>
                        <CellText>
                            <Text Element="span">
                                {text}
                                {NAME_SEPARATOR}
                            </Text>
                            <Text Element="span" style="secondary" typography="label-3-regular">
                                id
                                {NON_BREAKING_SPACE}
                                {value}
                            </Text>
                        </CellText>
                        <CellText type="subtitle">{areaName}</CellText>
                    </Cell>
                );
            },
            [isXS]
        );

    const customRenderContentBeforeWithClear: RenderContentBefore = ({ clearSelectedValuesAndClose }) => (
        <Card
            borderWidth="default"
            stretched
            hoverStyle="secondary"
            padding={16}
            borderRadius={16}
            onClick={clearSelectedValuesAndClose}
        >
            <Cell Element="div" align="center" left={<CrossOutlinedSize24 />}>
                <CellText maxLines={1}>Сбросить</CellText>
            </Cell>
        </Card>
    );
    const input = (
        <Select
            type="radio"
            searchable
            value={value}
            dataProvider={dataProvider}
            onChange={onChangeSelect}
            bottomSheetHeight="full-screen"
            onDropOpen={onOpenVacancies}
            onBottomSheetOpen={onOpenVacancies}
            onOptionsChange={(options) => {
                onSearch?.(options);
            }}
            triggerProps={{
                size: 'medium',
                label: trl(TrlKeys.placeholder),
                invalid: !!error,
                errorMessage: !!error && error,
                disabled: disabled || isDisabled,
                stretched: true,
                'data-qa': 'vacancies-select',
            }}
            renderDesktopItem={customRenderItem}
            renderMobileItem={customRenderItem}
            optionDataQa="select-vacancy-option"
            clearButton={
                withClearButton ? (
                    <Button mode="secondary" size="large" style="accent">
                        {trl(TrlKeys.clearButton)}
                    </Button>
                ) : undefined
            }
            onClear={resetVacancy}
            renderContentBefore={withClearButton && value ? customRenderContentBeforeWithClear : undefined}
        />
    );

    return (
        <>
            <MagritteFormField label={label || trl(TrlKeys.label)} input={input} />
            {under && (
                <>
                    <VSpacing default={8} />
                    {under}
                </>
            )}
            {vSpacingBottom && <VSpacing default={vSpacingBottom} />}
        </>
    );
};
